import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'

import styles from './article-topic-row.module.scss'
import { asVideoType } from '@app-types/video'
import { Container } from '@components/layout/grid'
import { SectionBlock } from '@components/layout/section-block'
import { Row } from '@components/row'
import { ChevronIcon } from '@components/svgs'
import { Text } from '@components/text'
import { Thumbnail } from '@components/thumbnail'
import type { ArticleTopicWithArticlesFragment } from '@graphql/fragments/ArticleTopicWithArticlesFragment.graphql'
import type { ArticleKind } from '@graphql/hooks/article-page'
import { useUIStore } from '@lib/store/useUIStore'
import { LINK } from '@shared/util/link'

/**
 * Renders a row of articles with the topic heading
 */
const ArticleTopicRow: FunctionComponent<{
    topic: ArticleTopicWithArticlesFragment
}> = ({ topic }) => {
    const router = useRouter()
    const { webview } = useUIStore()

    return (
        <div className={styles.row}>
            <Container>
                <SectionBlock align='start'>
                    <a
                        className={styles.heading}
                        href={LINK.money101Topic(
                            topic?.slug ?? '',
                            webview.isWebview,
                        ).serialize(router)}
                    >
                        <Text
                            tag='div'
                            typeStyle='heading-sm'
                            bold
                            className={styles.title}
                        >
                            {topic.title}
                        </Text>
                        <Text
                            tag='div'
                            color='dim'
                            typeStyle='heading-sm'
                            className={styles.count}
                        >
                            ( {topic.linkedFrom?.articles?.total ?? 0} )
                        </Text>
                        <div className={styles.chevron}>
                            <ChevronIcon />
                        </div>
                    </a>

                    <Row maxColumns={3} spacing='compact'>
                        {(topic.linkedFrom?.articles?.items ?? []).map(
                            (article, index) => {
                                return (
                                    <div
                                        className={styles.thumbnail}
                                        key={article.sys.id}
                                    >
                                        <Thumbnail
                                            title={article.title}
                                            subtitle={article.category?.title}
                                            image={article.image}
                                            href={
                                                article?.slug
                                                    ? LINK.post(
                                                          article.slug,
                                                          article.kind as ArticleKind,
                                                          webview.isWebview,
                                                      ).serialize(router)
                                                    : ''
                                            }
                                            fit='cover'
                                            video={
                                                article.video &&
                                                article.kind !== 'Video' // show preview image for Video, dont show video as thumbnail
                                                    ? asVideoType(article.video)
                                                    : undefined
                                            }
                                        />
                                    </div>
                                )
                            },
                        )}
                    </Row>
                </SectionBlock>
            </Container>
        </div>
    )
}

export default ArticleTopicRow
